<template>
  <div class="location__container">

    <div class="location__nav">
      <aside 
        v-if="warehouses.length > 0"
        class="menu">
        <v-select 
          v-model="warehouseId"
          :options="warehouses"
          :item-text="v => `${warehouse.warehouse_name} - ${warehouse.warehouse_code}`"
          item-value="id"
        />
        <v-fade-transition mode="out-in">
          <ul 
            :key="warehouseId" 
            class="menu-list mt-3">
            <li 
              v-for="navLocation in warehouse.locations" 
              :key="navLocation.id">
              <a 
                @click="changeLocation(navLocation)"
                :class="{ 'is-active': navLocation.id == location.id }">
                {{ navLocation.location_code }}
              </a>
            </li>
          </ul>
        </v-fade-transition>
      </aside>
    </div>

    <div class="location__details">

      <div 
        class="columns"
        v-if="!firstLoad">
        <div class="column is-one-quarter">
          <label class="label">Warehouse Name</label>
          <div>{{ location.warehouse.warehouse_name }}</div>
        </div>
        <div class="column is-one-quarter">
          <label class="label">Space Code</label>
          <div>{{ location.location_code }}</div>
        </div>
        <div class="column is-one-quarter">
          <label class="label">Space Name</label>
          <div>{{ location.location_name }}</div>
        </div>
        <div class="column is-one-quarter">
          <label class="label">Space Description</label>
          <div>{{ location.location_desc }}</div>
        </div>
      </div>

      <hr/>
<!-- 
      <button class="button is-primary mb-3">
        Add a Storage to this Location
      </button> -->

      <v-fade-transition mode="out-in">
        <article 
          v-if="!firstLoad && location.storages.length == 0" 
          class="message is-info is-fullwidth">
          <div class="message-body">
            There is nothing stored in this location.
          </div>
        </article>

        <table 
          v-if="!firstLoad && location.storages.length > 0"
          :key="location.id"
          class="table is-fullwidth is-hoverable">

          <thead>
            <tr>
              <!-- <th>
                <v-checkbox 
                  :value="allSelected"
                  @input="toggleAllSelected($event)"/>
              </th> -->
              <th 
                v-for="(header, headerIndex) in headers"
                :key="headerIndex">
                {{ header }}
              </th>
            </tr>
          </thead>

          <tbody>

            <template
              v-for="storage in location.storages">
              <tr
                class="v-centered subheader"
                :key="storage.id">
                <td>
                  {{ storage.storage_code }}
                </td>
                <td>
                  {{ storage.storage_name }}
                </td>
                <td>
                  {{ storage.storage_type.storage_type_name }}
                </td>
                <td>
                  {{ storage.storage_units.length }}
                </td>
                <td>
                  <div class="buttons buttons-right">
                    <button 
                      @click="changeStorageSpace(storage)"
                      class="button is-small">
                      <span class="icon">
                        <i class="fas fa-exchange-alt"></i>
                      </span>
                      <span>Change Storage Space</span>
                    </button>
                  </div>
                </td>
              </tr>

              <tr 
                v-if="storage.storage_units.length > 0"
                class="has-tiny-font has-background-light"
                :key="`${storage.id}-storage-units-header`">
                <th 
                  v-for="(header, headerIndex) in storageUnitHeaders"
                  :key="headerIndex">
                  {{ header }}
                </th>
              </tr>
              <tr 
                class="has-background-light v-centered"
                v-for="storageUnit in storage.storage_units"
                :key="`${storage.id}-${storageUnit.id}`">
                <td>
                  {{ storageUnit.stocking_unit.product_master.product_category.category }}
                </td>
                <td>
                  <span v-if="storageUnit.stocking_unit.product_master.product_subcategory">
                    {{ storageUnit.stocking_unit.product_master.product_subcategory.subcategory }}
                  </span>
                  <span v-else class="has-text-grey-light">
                    No subcategory
                  </span>
                </td>
                <td>
                  <div>{{ storageUnit.stocking_unit.product_master.name }}</div>
                  <div
                    class="help" 
                    v-if="storageUnit.stocking_unit.product_master.product_category.has_dimensions">
                    {{ lwt(storageUnit.stocking_unit) }}
                  </div>
                </td>
                <td>
                  {{ storageUnit.qty }}
                </td>
                <td>
                  <div class="buttons buttons-right">
                    <button 
                      @click="moveStorageUnit(storage, storageUnit)"
                      class="button is-small">
                      <span class="icon">
                        <i class="fas fa-exchange-alt"></i>
                      </span>
                      <span>Move To Different Storage</span>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            
          </tbody>

        </table>
      </v-fade-transition>

    </div>

    <move-warehouse-item-modal
      v-if="!firstLoad"
      v-model="moving.state"
      :storage-unit="moving.storageUnit"
      :current-storage="moving.storage"
      :current-location="location"/>

    <change-storage-location-modal
      v-if="changing.storage"
      v-model="changing.state"
      :storage="changing.storage"
      :current-location="location"/>

  </div>
</template>

<script>
// import VCheckbox from '@/components/base/VCheckbox'
import VSelect from '@/components/base/VSelect'
import MoveWarehouseItemModal from '@/components/warehousing/MoveWarehouseItemModal'
import ChangeStorageLocationModal from '@/components/warehousing/ChangeStorageLocationModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    // VCheckbox,
    VSelect,
    MoveWarehouseItemModal,
    ChangeStorageLocationModal,
    VFadeTransition
  },
  data() {
    return {
      headers: [
        'Storage Code', 'Storage Name', 'Storage Type', 'Total Items', ''
      ],
      storageUnitHeaders: [
        'Product Category', 'Product Subcategory', 'Product', 'Quantity', ''
      ],
      moving: {
        state: false,
        storage: null,
        storageUnit: null
      },
      changing: {
        state: false,
        storage: null
      },
      warehouseId: null
    }
  },
  computed: {
    ...mapState("warehouses", ["warehouses"]),
    ...mapState("warehouseLocation", ["location", "firstLoad"]),
    warehouse({ warehouseId }) {
      return this.warehouses.filter(d => d.id == warehouseId)[0];
    }
  },
  watch: {
    '$route.params.locationId'(id) {
      this.getLocation(id);
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("warehouseLocation", ["getLocation"]),
    ...mapActions("warehouses", ["getWarehouses"]),
    ...mapMutations("warehouseLocation", ["setFirstLoad", "setLocation"]),
    initialize() {
      let location = this.$route.params.location;
      if (location) {
        this.setLocation(location);
        this.warehouseId = location.warehouse.id;
        this.setFirstLoad(false);
      } else {
        this.getLocation(this.$route.params.locationId).then(() => {
          this.warehouseId = this.location.warehouse.id;
        })
      }

      if (this.warehouses.length == 0) {
        this.getWarehouses();
      }
    },
    lwt(v) {
      return `L${v.length_in_str}" x W${v.width_in_str} x T${v.thick_in_str}`
    },
    changeLocation(location) {
      this.$router.push({ 
        name: 'WarehouseLocationViewer',
        params: {
          locationId: location.id,
          location: location
        } 
      })
    },
    moveStorageUnit(storage, storageUnit) {
      this.moving.state = true;
      this.moving.storage = storage;
      this.moving.storageUnit = storageUnit;
    },
    changeStorageSpace(storage) {
      this.changing.state = true;
      this.changing.storage = storage;
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteLocation(id);
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} locations?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteLocation(id);
        }
        this.confirmDelete.message = 'Are you sure you want to delete this location?'
      }
      this.confirmDelete.state = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.location {
  &__container {
    display: flex;
    flex-direction: row;
    min-height: calc(100vh - 52px);
  }
  &__nav {
    width: 15%;
    padding: 1.5em 1em;
    height: calc(100vh - 52px);
    overflow-y: auto;
    border-right: 1px solid gainsboro;
  }
  &__details {
    width: 85%;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    padding-left: 1em;
    padding-right: 1.5em;
    overflow-y: auto;
  }
}
</style>